import { CASCADE_ENUM } from '../../../../services/enum.service';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import {
  IAgGridRadio,
  RadioButtonCellRendered,
} from '../../../../utils/custom-renderer/radio-button-cell-renderer';
import { ICONS } from '../../../../utils';
import {
  getFillColorFromProjectStatus,
  getFormattedDate,
  getFormattedNumber,
} from '../../../../utils/formatting';
import type { ProjectListing } from './project-listing';
import { getTechnologyIconName } from '../../../../styles';
import { CUSTOM_HEADER_TOOLTIPS, headerWithTooltipColParams } from '../../../../utils/grid';

export enum COLUMN_FIELDS {
  PROJECT_NAME = 'NAME',
  TECHNOLOGY = 'TECHNOLOGY',
  ISO = 'ISO',
  STATE = 'STATE_ABBREVIATION',
  COUNTY = 'COUNTY_NAME',
  NAMEPLATE_CAPACITY = 'MEGAWATT_AC',
  GUARANTEED_COD = 'GUARANTEED_COD',
  PROJECT_STATUS = 'PROJECT_STATUS',
}

export function PROJECT_LISTING_COLUMNS(context: ProjectListing) {
  return [
    {
      maxWidth: 35,
      cellRenderer: RadioButtonCellRendered,
      cellRendererParams: (params) => {
        const agGridRadioParams: IAgGridRadio = {
          isSelected: false,
          project: params.data,
          callback: (detail) => {
            context.selectProject(detail);
          },
        };
        if (context.selectedProjectId && context.selectedProjectId === params.data.PROJECT_ID) {
          agGridRadioParams.isSelected = true;
        }
        return agGridRadioParams;
      },
      lockPosition: 'left',
      flex: 1,
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.PROJECT_NAME,
      headerName: 'Project',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      lockPinned: true,
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        const obj = params.data;
        const hyperlinkParams: IGridHyperlink = {
          value: obj.NAME,
          id: null,
          vppaId: null,
          iconList: [],
        };
        if (obj.PROJECT_ID && obj.PROJECT_ID !== 'DEFAULT') {
          hyperlinkParams.id = obj.PROJECT_ID;

          const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];

          const iconName = getTechnologyIconName(obj.TECHNOLOGY);
          iconList.push({
            icon: iconName,
            width: '13px',
            height: '16px',
            fill: getFillColorFromProjectStatus(obj.PROJECT_OPERATIONAL_STATUS),
          });

          hyperlinkParams.iconList = iconList;
        }
        return hyperlinkParams;
      },
      lockPosition: 'left',
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.ICON_LEGEND),
    },
    {
      field: COLUMN_FIELDS.TECHNOLOGY,
      headerName: 'Technology',
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      lockPinned: true,
      valueFormatter: (params) =>
        context.enumService.getEnumTranslation(CASCADE_ENUM.TECHNOLOGY, params.data.TECHNOLOGY),
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.STATE,
      headerName: 'State',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.COUNTY,
      headerName: 'County',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      lockPinned: true,
    },
    {
      field: COLUMN_FIELDS.NAMEPLATE_CAPACITY,
      headerName: 'Nameplate Capacity (MW)',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      lockPinned: true,
      valueFormatter: (params) => getFormattedNumber(params.data.MEGAWATT_AC),
    },
    {
      field: COLUMN_FIELDS.GUARANTEED_COD,
      headerName: 'Guaranteed COD',
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      lockPinned: true,
      valueFormatter: (params) => getFormattedDate(params.data.GUARANTEED_COD),
    },
    {
      field: COLUMN_FIELDS.PROJECT_STATUS,
      headerName: 'Status',
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      lockPinned: true,
      filterValueGetter: (params) =>
        context.enumService.getEnumTranslation(
          CASCADE_ENUM.PROJECT_STATUS,
          params.data.PROJECT_STATUS,
        ),
      valueFormatter: (params) =>
        context.enumService.getEnumTranslation(
          CASCADE_ENUM.PROJECT_STATUS,
          params.data.PROJECT_STATUS,
        ),
    },
  ];
}
